import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { resolveLandingRoute } from '../config/navigation';
import { buildMenuAccessChecker } from '../utils/permissions';

interface AuthGuardProps {
  requireAuth?: boolean;
  requiredRoles?: string[];
}

const AuthGuard: React.FC<AuthGuardProps> = ({
  requireAuth = true,
  requiredRoles = []
}) => {
  const { isAuthenticated, isInitializing, user, permissions } = useAuth();
  const location = useLocation();
  // ตรงกับ bypass ฝั่ง backend + usePermission (BUG-SA37: backend ส่ง is_superuser เท่านั้น)
  // G-02: bypass model เดียวกันทั้ง 4 ชั้น (ProtectedRoute, AuthContext.hasPermission, AuthGuard ตัวนี้, buildMenuAccessChecker)
  const isSuperAdmin = Boolean(user?.is_superadmin || user?.role === 'superadmin');
  const isSuperUser = Boolean(user?.is_superuser);

  // แสดงตัวโหลดขณะกำลังตรวจสอบสถานะการยืนยันตัวตน (เฉพาะตอน bootstrap)
  // BUG-AUTH-REMOUNT FIX: เดิมผูกกับ isLoading ซึ่ง login() ก็ตั้งด้วย — /login อยู่ใต้
  // <AuthGuard requireAuth={false}> ทุกครั้งที่กดเข้าสู่ระบบจึงถูกถอดทิ้งแล้ว mount ใหม่
  // → username ที่พิมพ์ไว้หาย + focus เด้งกลับช่องแรกเมื่อรหัสผิด
  if (isInitializing) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  // ถ้าต้องการยืนยันตัวตนแต่ผู้ใช้ยังไม่ได้ยืนยันตัวตน ให้เปลี่ยนเส้นทางไปยังหน้าล็อกอิน
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // ถ้าผู้ใช้ยืนยันตัวตนแล้วแต่เส้นทางนี้สำหรับผู้ที่ยังไม่ได้ยืนยันตัวตนเท่านั้น
  if (!requireAuth && isAuthenticated) {
    // G-26 (PO 2026-08-24): login แล้วไปหน้า landing แรกเสมอ — ยกเลิกการ restore fromState
    // (FINDING-S453-001 เดิม) เพราะ fromState จำหน้าสุดท้ายของ "ผู้ใช้คนก่อน" ข้าม session ได้
    // → login ใหม่แล้วเด้งไปหน้าค้างเก่า (บางกรณีเป็นหน้าที่ user ใหม่ไม่มีสิทธิ์ → access-denied)
    // P-S616-1: ใช้ resolver เลือกเมนูแรกที่ role เปิดได้จริง (dashboard เป็นอันดับแรกเสมอถ้ามีสิทธิ์)
    const target = resolveLandingRoute(buildMenuAccessChecker(permissions, isSuperAdmin || isSuperUser));
    return <Navigate to={target} replace />;
  }

  // ตรวจสอบสิทธิ์ตามบทบาท
  if (requireAuth && isAuthenticated && requiredRoles.length > 0) {
    // superadmin เข้าได้ทุกหน้า · superuser เข้า admin routes ได้ (คำนวณไว้ด้านบนแล้ว)
    // ถ้าไม่ใช่ superadmin/superuser ให้ตรวจสอบบทบาทตามปกติ
    const hasRequiredRole = isSuperAdmin || isSuperUser || (user && (
      requiredRoles.includes(user.role || '') ||
      (user.roles && user.roles.some((role: string) => requiredRoles.includes(role)))
    ));

    if (!hasRequiredRole) {
      // เปลี่ยนเส้นทางไปยังหน้าไม่มีสิทธิ์เข้าถึง
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // ถ้าผ่านการตรวจสอบทั้งหมด แสดงคอมโพเนนต์ที่ป้องกัน
  return <Outlet />;
};

export default AuthGuard;